import { Info } from "lucide-react";
import { useEffect, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import useApiRequest from "../../../lib/hooks/useRequest";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import FileDropZone from "../FileDropZone";
import { OnboardingFormData } from "../onboarding-schema";

interface MediaStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const MediaStep = ({ form }: MediaStepProps) => {
  const { apiFileRequest, apiRequest } = useApiRequest();
  const [lastUploadedLogoKey, setLastUploadedLogoKey] = useState<string | null>(null);
  const renderTeamInput = false;
  const logoFiles = useWatch({
    control: form.control,
    name: "logoFiles",
  });

  useEffect(() => {
    let cancelled = false;

    const uploadOrDeleteLogo = async () => {
      const logo = (logoFiles ?? [])[0];
      if (!logo) {
        if (lastUploadedLogoKey) {
          const response = await apiRequest("onboarding/upload/organization-logo", "DELETE", {
            toast: { toastText: "Logo entfernt" },
          });
          if (process.env.NODE_ENV !== "production") {
            console.log("[MediaStep] logo delete response", {
              status: response.status,
              hasError: Boolean(response.error),
            });
          }
          if (!cancelled && !response.error) {
            setLastUploadedLogoKey(null);
          }
        }
        return;
      }

      // Include previewUrl so selecting the same file again can still retrigger upload.
      const logoKey = `${logo.name}-${logo.size}-${logo.type}-${logo.previewUrl}`;
      if (logoKey === lastUploadedLogoKey) return;

      const blob = await fetch(logo.previewUrl).then((res) => res.blob());
      const file = new File([blob], logo.name, { type: logo.type || blob.type });
      const response = await apiFileRequest<string>(`onboarding/upload/organization-logo`, "POST", file, {
        formdataName: "file",
      });
      if (process.env.NODE_ENV !== "production") {
        console.log("[MediaStep] logo upload response", {
          status: response.status,
          hasError: Boolean(response.error),
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });
      }
      if (!cancelled && !response.error) {
        setLastUploadedLogoKey(logoKey);
      }
    };

    uploadOrDeleteLogo();
    return () => {
      cancelled = true;
    };
  }, [apiFileRequest, apiRequest, lastUploadedLogoKey, logoFiles]);

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 8 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Zeigen Sie, wer Sie sind.</h1>
      <p className="text-sm text-gray-500 mb-6">
        Hochwertige Bilder und ein professionelles Logo stärken Ihre Arbeitgebermarke und erhöhen das Vertrauen der
        Bewerber.
      </p>

      <div className="space-y-6">
        {/* Section 1: Firmenlogo */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Firmenlogo</h2>
          <p className="text-sm text-gray-500 mb-6">
            Ein hochwertiges Logo sorgt für Wiedererkennung, Professionalität und stärkt das Vertrauen der Bewerber.
            Format: PNG, JPG oder SVG.
          </p>

          <FormField
            control={form.control}
            name="logoFiles"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FileDropZone
                    accept="image/png,image/jpeg,image/svg+xml"
                    maxSizeBytes={5 * 1024 * 1024}
                    multiple={false}
                    icon="upload"
                    label="Logo hierher ziehen oder klicken zum Auswählen"
                    hint="PNG, JPG oder SVG · Max. 5 MB"
                    files={field.value ?? []}
                    onFilesChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Section 2: Team-Fotos & Videos */}
        {renderTeamInput && (
          <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Team-Fotos & Videos</h2>
            <p className="text-sm text-gray-500 mb-6">
              Authentische Bilder aus dem Arbeitsalltag erhöhen die Glaubwürdigkeit. Echte Einblicke wirken besser als
              Studiofotos.
            </p>

            <FormField
              control={form.control}
              name="mediaFiles"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FileDropZone
                      accept="image/png,image/jpeg,video/mp4"
                      maxSizeBytes={10 * 1024 * 1024}
                      multiple={true}
                      icon="image"
                      label="Bilder und Videos hierher ziehen oder klicken zum Auswählen"
                      hint="JPG, PNG, MP4 · Mehrere Dateien möglich · Max. 10 MB pro Datei"
                      files={field.value ?? []}
                      onFilesChange={field.onChange}
                      showPreviews={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Section 3: Firmenfarbe */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Firmenfarbe</h2>
          <p className="text-sm text-gray-500 mb-6">
            Ihre Unternehmensfarbe wird für ein einheitliches Branding in Stellenanzeigen und auf Ihrer Karriereseite
            verwendet.
          </p>

          <FormField
            control={form.control}
            name="ciColorPrimary"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Farbe wählen</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="color"
                      value={field.value || "#0099ce"}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-10 w-14 cursor-pointer rounded border border-gray-200 p-0.5"
                    />
                    <Input
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="#000000"
                      className="max-w-[140px] font-mono"
                    />
                    {field.value && (
                      <div
                        className="h-10 flex-1 rounded-md border border-gray-200"
                        style={{ backgroundColor: field.value }}
                      />
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Tip box */}
        <div
          className="flex items-start gap-3 rounded-lg p-4 border-l-[3px]"
          style={{
            backgroundColor: "rgba(0, 153, 206, 0.05)",
            borderLeftColor: "#0099ce",
          }}
        >
          <Info className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#0099ce" }} />
          <div>
            <p className="text-sm font-medium text-gray-900">Tipp:</p>
            <p className="text-sm text-gray-600">
              Fotos vom Team, den Räumlichkeiten oder typischen Arbeitssituationen wirken besonders authentisch.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaStep;
